import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FaDownload, FaFileAlt, FaFilePdf, FaPen, FaPlus, FaSearch, FaSyncAlt, FaTrashAlt, FaUpload } from 'react-icons/fa';
import API_BASE_URL from './config/api';
import './MaintenanceDocuments.css';

const MAX_FILE_SIZE = 15 * 1024 * 1024;
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('managerToken') || ''}` });

async function responseError(response, fallback) {
  const body = await response.json().catch(() => ({}));
  return body.error || body.msg || fallback;
}

function fileSize(size) {
  return size >= 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(size / 1024))} KB`;
}

function addedDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function MaintenanceDocuments() {
  const [source, setSource] = useState({ loading: true, documents: [], canManage: false, error: '' });
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uncertainSave, setUncertainSave] = useState(false);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(async (signal) => {
    setSource(previous => ({ ...previous, loading: true, error: '' }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/maintenance/documents`, { headers: authHeaders(), signal });
      if (!response.ok) throw new Error(await responseError(response, 'Documents could not be loaded.'));
      const data = await response.json();
      if (!Array.isArray(data.documents)) throw new Error('Documents could not be loaded.');
      if (!signal?.aborted) {
        setSource({ ...data, loading: false, error: '' });
        setNeedsRefresh(false);
      }
    } catch (error) {
      if (!signal?.aborted) setSource(previous => ({ ...previous, loading: false, error: error.message }));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const managementDisabled = source.loading || needsRefresh || saving;
  const filtered = useMemo(() => source.documents.filter(document => (
    `${document.title} ${document.fileName}`.toLowerCase().includes(search.trim().toLowerCase())
  )), [source.documents, search]);

  const openEditor = document => {
    setTitle(document?.title || ''); setFile(null); setFormError(''); setUncertainSave(false); setEditor({ document });
  };
  const openAdd = () => openEditor(null);

  const selectFile = event => {
    const selected = event.target.files?.[0] || null;
    setFile(selected); setFormError('');
    if (selected && !title.trim()) setTitle(selected.name.replace(/\.[^.]+$/, ''));
  };

  const saveDocument = async event => {
    event.preventDefault();
    if (saving || uncertainSave || needsRefresh) return;
    const existing = editor?.document;
    if (!title.trim() || (!existing && !file)) { setFormError(existing ? 'Enter a document name.' : 'Enter a document name and choose a file.'); return; }
    if (file && file.size > MAX_FILE_SIZE) { setFormError('Choose a file smaller than 15 MB.'); return; }
    if (file && !file.size) { setFormError('This file is empty. Choose another file.'); return; }
    setSaving(true); setFormError(''); setNotice('');
    const body = new FormData();
    body.append('title', title.trim());
    if (file) body.append('file', file);
    let response;
    try {
      const path = existing ? `/api/maintenance/documents/${encodeURIComponent(existing.id)}` : '/api/maintenance/documents';
      response = await fetch(`${API_BASE_URL}${path}`, { method: existing ? 'PATCH' : 'POST', headers: authHeaders(), body });
      if (!response.ok) throw new Error(await responseError(response, 'The document could not be saved. Please try again.'));
      const data = await response.json();
      if (!data.document?.id || (existing && data.document.id !== existing.id)) throw new Error('The save could not be confirmed.');
      setSource(previous => ({ ...previous, documents: existing ? previous.documents.map(document => document.id === existing.id ? data.document : document) : [data.document, ...previous.documents] }));
      setSearch(''); setEditor(null); setNotice(existing ? 'Document updated.' : 'Document added.');
    } catch (error) {
      if (!response || response.status >= 500 || response.ok) {
        setUncertainSave(true); setNeedsRefresh(true);
        setFormError('The save could not be confirmed. Close this window and refresh Documents before trying again.');
      } else { setFormError(error.message); }
    }
    finally { setSaving(false); }
  };

  const removeDocument = async () => {
    if (!removing || saving || uncertainSave || needsRefresh) return;
    setSaving(true); setFormError(''); setNotice('');
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/api/maintenance/documents/${encodeURIComponent(removing.id)}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error(await responseError(response, 'The document could not be removed.'));
      const data = await response.json();
      if (data.document?.id !== removing.id) throw new Error('The removal could not be confirmed.');
      setSource(previous => ({ ...previous, documents: previous.documents.filter(document => document.id !== removing.id) }));
      setRemoving(null); setNotice('Document removed.');
    } catch (error) {
      if (!response || response.status >= 500 || response.ok) {
        setUncertainSave(true); setNeedsRefresh(true);
        setFormError('The removal could not be confirmed. Close this window and refresh Documents before trying again.');
      } else { setFormError(error.message); }
    } finally { setSaving(false); }
  };

  const download = async document => {
    setDownloading(document.id); setDownloadError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/maintenance/documents/${encodeURIComponent(document.id)}/file`, { headers: authHeaders() });
      if (!response.ok) throw new Error(await responseError(response, 'This document could not be downloaded.'));
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url; link.download = document.fileName; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { setDownloadError(error.message); }
    finally { setDownloading(null); }
  };

  return <main className="internal-documents">
    <header className="internal-documents__header">
      <div><h1>Documents</h1><p>Important company documents, saved for reference.</p></div>
      <div className="internal-documents__actions">
        <button className="internal-documents__button" type="button" onClick={() => load()} disabled={source.loading || saving}><FaSyncAlt aria-hidden="true" /> Refresh</button>
        {source.canManage && <button className="internal-documents__button internal-documents__button--primary" type="button" onClick={openAdd} disabled={managementDisabled}><FaPlus aria-hidden="true" /> Add document</button>}
      </div>
    </header>
    {source.storageMode === 'local' && <p className="internal-documents__preview">Local preview — uploads here are kept separate from the live dashboard.</p>}
    {notice && <p className="internal-documents__notice" role="status">{notice}</p>}
    {downloadError && <p className="internal-documents__error" role="alert">{downloadError}</p>}
    {needsRefresh && !editor && !removing && <p className="internal-documents__preview" role="status">Refresh Documents to confirm the latest changes before making another change.</p>}
    <section className="internal-documents__library" aria-label="Company documents">
      <div className="internal-documents__toolbar">
        <h2 className="internal-documents__library-title">All documents{!source.loading && !source.error && <span>{source.documents.length}</span>}</h2>
        <label className="internal-documents__search"><FaSearch aria-hidden="true" /><input aria-label="Search documents" placeholder="Search documents…" value={search} onChange={event => setSearch(event.target.value)} /></label>
      </div>
      {source.loading ? <div className="internal-documents__empty" role="status">Loading documents…</div> : source.error ? <div className="internal-documents__empty" role="alert"><p>{source.error}</p><button className="internal-documents__button" type="button" onClick={() => load()}>Try again</button></div> : !filtered.length ? <div className="internal-documents__empty">
        <span className="internal-documents__empty-icon"><FaFileAlt aria-hidden="true" /></span>
        <h2>{source.documents.length ? 'No matching documents' : 'Keep important documents here'}</h2>
        <p>{source.documents.length ? 'Try another document name.' : source.canManage ? 'Upload a file and give it a name so your team can find it later.' : 'Documents added by your team will appear here.'}</p>
        {!source.documents.length && source.canManage && <button className="internal-documents__button internal-documents__button--primary" type="button" onClick={openAdd} disabled={managementDisabled}><FaPlus aria-hidden="true" /> Add document</button>}
      </div> : <table className="internal-documents__table">
        <thead><tr><th>Document</th><th>Added</th><th><span className="internal-documents__sr-only">Actions</span></th></tr></thead>
        <tbody>{filtered.map(document => <tr key={document.id}>
          <td><div className="internal-documents__file"><span className={`internal-documents__file-icon${document.contentType === 'application/pdf' ? ' internal-documents__file-icon--pdf' : ''}`}>{document.contentType === 'application/pdf' ? <FaFilePdf aria-hidden="true" /> : <FaFileAlt aria-hidden="true" />}</span><div><strong>{document.title}</strong><small>{document.fileName} · {fileSize(document.size)}</small></div></div></td>
          <td><time dateTime={document.createdAt}>{addedDate(document.createdAt)}</time>{document.uploadedBy && <small>{document.uploadedBy}</small>}</td>
          <td><div className="internal-documents__row-actions">
            <button className="internal-documents__button internal-documents__download" type="button" onClick={() => download(document)} disabled={downloading === document.id || saving} aria-label={`Download ${document.title}`} title={`Download ${document.title}`}><FaDownload aria-hidden="true" /><span>{downloading === document.id ? 'Downloading…' : 'Download'}</span></button>
            {source.canManage && <><button className="internal-documents__button internal-documents__button--icon" type="button" aria-label={`Edit ${document.title}`} title="Edit document" disabled={managementDisabled} onClick={() => openEditor(document)}><FaPen aria-hidden="true" /></button><button className="internal-documents__button internal-documents__button--icon internal-documents__button--remove" type="button" aria-label={`Remove ${document.title}`} title="Remove document" disabled={managementDisabled} onClick={() => { setRemoving(document); setFormError(''); setUncertainSave(false); }}><FaTrashAlt aria-hidden="true" /></button></>}
          </div></td>
        </tr>)}</tbody>
      </table>}
    </section>
    <Modal show={Boolean(editor)} onHide={() => { if (!saving) setEditor(null); }} centered backdrop={saving ? 'static' : true} keyboard={!saving} aria-labelledby="edit-document-title" contentClassName="internal-documents__modal">
      <form onSubmit={saveDocument}>
        <Modal.Header closeButton={!saving}><Modal.Title id="edit-document-title">{editor?.document ? 'Edit document' : 'Add document'}</Modal.Title></Modal.Header>
        <Modal.Body>
          {formError && <p className="internal-documents__error" role="alert">{formError}</p>}
          <label htmlFor="document-title">Document name</label>
          <input id="document-title" value={title} onChange={event => setTitle(event.target.value)} maxLength={160} placeholder="Enter a document name" required disabled={saving || uncertainSave} />
          {editor?.document && <p className="internal-documents__current-file">Current file: <strong>{editor.document.fileName}</strong><span>Keep this file, or choose a replacement below.</span></p>}
          <label htmlFor="document-file" className="internal-documents__upload"><FaUpload aria-hidden="true" /><strong>{file ? file.name : editor?.document ? 'Choose a replacement file (optional)' : 'Choose a file'}</strong><span>PDF, image, or Office document · Up to 15 MB</span></label>
          <input ref={fileInput} key={editor?.document?.id || 'new'} id="document-file" aria-label={editor?.document ? 'Replacement file (optional)' : 'Document file'} type="file" accept={ACCEPT} onChange={selectFile} disabled={saving || uncertainSave} required={!editor?.document} />
          {editor?.document && file && <button className="internal-documents__keep-file" type="button" disabled={saving || uncertainSave} onClick={() => { setFile(null); if (fileInput.current) fileInput.current.value = ''; }}>Keep current file instead</button>}
        </Modal.Body>
        <Modal.Footer><button className="internal-documents__button" type="button" disabled={saving} onClick={() => setEditor(null)}>{uncertainSave ? 'Close' : 'Cancel'}</button><button className="internal-documents__button internal-documents__button--primary" type="submit" disabled={saving || uncertainSave}>{saving ? editor?.document ? 'Saving…' : 'Uploading…' : editor?.document ? 'Save changes' : 'Add document'}</button></Modal.Footer>
      </form>
    </Modal>
    <Modal show={Boolean(removing)} onHide={() => { if (!saving) setRemoving(null); }} centered backdrop={saving ? 'static' : true} keyboard={!saving} aria-labelledby="remove-document-title" contentClassName="internal-documents__modal">
      <Modal.Header closeButton={!saving}><Modal.Title id="remove-document-title">Remove document</Modal.Title></Modal.Header>
      <Modal.Body>
        {formError && <p className="internal-documents__error" role="alert">{formError}</p>}
        <p>Remove <strong>{removing?.title}</strong> from the library?</p>
        <p className="internal-documents__remove-hint">It will no longer be available in Documents.</p>
      </Modal.Body>
      <Modal.Footer><button className="internal-documents__button" type="button" disabled={saving} onClick={() => setRemoving(null)}>{uncertainSave ? 'Close' : 'Cancel'}</button><button className="internal-documents__button internal-documents__button--danger" type="button" disabled={saving || uncertainSave} onClick={removeDocument}>{saving ? 'Removing…' : 'Remove document'}</button></Modal.Footer>
    </Modal>
  </main>;
}
